import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	Component,
	computed,
	input,
	output,
	signal,
	Signal,
	OnInit,
	effect,
	PLATFORM_ID,
	Inject,
	AfterViewInit
} from '@angular/core';
import { Router } from '@angular/router';
import { StoreService } from '@sp-client/app/services/store.service';
import { ModalComponent } from '../modal/modal.component';
import { StoreSelectPlanComponent } from '../store-select-plan/store-select-plan.component';
import { StoreCheckoutComponent } from '../store-checkout/store-checkout.component';
import { StoreReceiptComponent } from '../store-receipt/store-receipt.component';

@Component({
	selector: 'sp-upgrade-modal',
	imports: [CommonModule, ModalComponent, StoreSelectPlanComponent, StoreCheckoutComponent, StoreReceiptComponent],
	templateUrl: './upgrade-modal.component.html',
	styleUrl: './upgrade-modal.component.scss'
})
export class UpgradeModalComponent implements OnInit, AfterViewInit {
	// Optional external control
	isVisible = input<Signal<boolean>>();

	// Internal signal for self-contained mode
	private _isVisible = signal(false);

	// Computed signal that uses external if provided, otherwise internal
	modalVisible = computed(() => {
		const externalSignal = this.isVisible();
		return externalSignal ? externalSignal() : this._isVisible();
	});

	// Track which step we're on: 'select-plan', 'checkout', or 'receipt'
	currentStep = signal<'select-plan' | 'checkout' | 'receipt'>('select-plan');
	private previousStep: 'select-plan' | 'checkout' | 'receipt' | null = null;

	// Store selected product from stripe-select-plan
	// This is kept for backward compatibility but syncs with the service
	private _selectedProduct = signal<string>('');
	selectedProduct = computed(() => {
		// Prefer service value, fallback to local signal
		const serviceValue = this.storeService.getSelectedProductValueSignal()();
		return serviceValue || this._selectedProduct();
	});

	// Store receipt data from successful checkout
	receiptData = signal<{
		orderNumber?: string | null;
		name: string;
		billingAddress: {
			line1?: string;
			line2?: string;
			city?: string;
			state?: string;
			postal_code?: string;
			country?: string;
		};
		cardLast4?: string;
		subtotal: number;
		tax: number;
		total: number;
		currency: string;
	} | null>(null);

	// Output for parent notification
	closed = output();

	previousRoute = input<string>('');

	pricing = this.storeService.getPricingSignal();
	amount = computed(() => {
		const pricingData = this.pricing();
		return pricingData?.oneTime || 0;
	});
	currency = computed(() => {
		const pricingData = this.pricing();
		return pricingData?.currency?.toLowerCase() || 'usd';
	});

	// Determine payment type from selected product
	paymentType = computed(() => {
		const product = this.selectedProduct();
		// Both monthly and yearly are recurring subscription products.
		return product === 'monthly' || product === 'yearly' ? 'subscription' : 'one-time';
	});

	constructor(
		private router: Router,
		private storeService: StoreService,
		@Inject(PLATFORM_ID) private platformId: object
	) {
		// Watch for step changes and scroll to top
		effect(() => {
			// Access currentStep to create dependency
			const step = this.currentStep();
			// Scroll to top when step changes (but not on initial load)
			if (isPlatformBrowser(this.platformId) && this.previousStep !== null && this.previousStep !== step) {
				// Use setTimeout to ensure DOM is updated after step change
				setTimeout(() => {
					this.scrollToTop();
				}, 100);
			}
			// Update previous step
			this.previousStep = step;
		});

		// Sync service selectedProduct changes to local signal
		effect(() => {
			const serviceValue = this.storeService.getSelectedProductValueSignal()();
			if (serviceValue !== this._selectedProduct()) {
				this._selectedProduct.set(serviceValue);
			}
		});

		// When modal becomes visible (e.g. via service), reset state so both open() and external signal paths work
		let wasVisible = this.modalVisible();
		effect(() => {
			const visible = this.modalVisible();
			if (visible && !wasVisible) {
				this.resetForOpen();
			}
			wasVisible = visible;
		});
	}

	ngOnInit(): void {
		// Store current route when modal opens
		if (this.modalVisible()) {
			this.storePreviousRoute();
		}
		// Pricing is automatically loaded via signal from store service
		// No need to call loadPricing() - the signal will fetch on first access
	}

	ngAfterViewInit(): void {
		// Scroll to top when modal first opens
		if (isPlatformBrowser(this.platformId) && this.modalVisible()) {
			setTimeout(() => {
				this.scrollToTop();
			}, 100);
		}
	}

	/**
	 * Scrolls the modal content to the top
	 */
	private scrollToTop(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		// Use multiple attempts with increasing delays to ensure DOM is ready
		const attemptScroll = (attempt: number = 0) => {
			if (attempt > 10) return; // Max 10 attempts

			// Try multiple selectors to find the scrollable container
			// The modal structure is: sp-modal > .slide-modal (scrollable) > .content > modal-step-container
			const selectors = [
				'.slide-modal', // Main scrollable container
				'.slide-modal .content', // Content wrapper
				'sp-modal .slide-modal' // With component selector
			];

			for (const selector of selectors) {
				const element = document.querySelector(selector) as HTMLElement;
				if (element) {
					// Always try to scroll, even if scrollTop is 0 (element might become scrollable)
					element.scrollTo({ top: 0, behavior: 'smooth' });
					// Also set scrollTop directly as immediate fallback
					element.scrollTop = 0;

					// If we successfully scrolled (or it was already at top), we're done
					if (element.scrollTop === 0) {
						return;
					}
				}
			}

			// If we haven't found the element yet, try again with a delay
			if (attempt < 10) {
				setTimeout(() => attemptScroll(attempt + 1), 100 * (attempt + 1));
			}
		};

		// Start with requestAnimationFrame for immediate attempt
		requestAnimationFrame(() => {
			attemptScroll(0);
		});
	}

	// Public methods
	open(): void {
		this._isVisible.set(true);
		this.resetForOpen();
	}

	/**
	 * Reset modal state when opening (step, selected product, receipt, previous route).
	 * Used by both open() and when opened via external isVisible signal (e.g. UpgradeModalService).
	 */
	private resetForOpen(): void {
		this.currentStep.set('select-plan');
		this.storeService.clearSelectedProduct();
		this._selectedProduct.set('');
		this.receiptData.set(null);
		this.storePreviousRoute();
	}

	close(): void {
		this._isVisible.set(false);
		this.closed.emit();
	}

	onModalClosed(): void {
		this.close();
	}

	onProceedToCheckout(selectedProduct: string): void {
		// Store selected product in service and local signal, then switch to checkout step
		this.storeService.setSelectedProduct(selectedProduct);
		this._selectedProduct.set(selectedProduct);
		this.currentStep.set('checkout');
		// Scroll to top after step change
		if (isPlatformBrowser(this.platformId)) {
			setTimeout(() => {
				this.scrollToTop();
			}, 150);
		}
	}

	onPaymentSuccess(): void {
		// Called when user clicks "Back to Dashboard" on receipt; close modal (receipt only emits, parent closes)
		this.close();
	}

	onCheckoutError(_error: Error): void {
		// Handle error - could show error message to user
		// Error is already logged by StripeCheckoutComponent or StripeSelectPlanComponent
	}

	onCheckoutBack(): void {
		// Go back to select-plan step
		this.currentStep.set('select-plan');
		// Scroll to top after step change
		if (isPlatformBrowser(this.platformId)) {
			setTimeout(() => {
				this.scrollToTop();
			}, 150);
		}
	}

	onCheckoutSucceeded(receipt: {
		orderNumber?: string | null;
		name: string;
		billingAddress: {
			line1?: string;
			line2?: string;
			city?: string;
			state?: string;
			postal_code?: string;
			country?: string;
		};
		cardLast4?: string;
		subtotal: number;
		tax: number;
		total: number;
		currency: string;
	}): void {
		// Store receipt data and switch to receipt step
		this.receiptData.set(receipt);
		this.currentStep.set('receipt');
		// Scroll to top after step change
		if (isPlatformBrowser(this.platformId)) {
			setTimeout(() => {
				this.scrollToTop();
			}, 150);
		}
		// Don't close modal - receipt page will handle that when user clicks "Done"
	}

	private storePreviousRoute(): void {
		const route = this.previousRoute() || this.router.url;
		this.storeService.setPreviousRoute(route);
	}
}
